import React, { FC, useEffect, useState } from 'react'

import styled from 'styled-components'

import {
  InternalUserListTableBody,
  InternalUserListTableHead,
  AddUserButton,
  TableSortType,
  InternalUserSortItemType,
  NotExistsUserMessage,
} from 'src/components/molecules'
import { BulkActionButton } from 'src/components/molecules/inputs/BulkActionButton'
import { CreateUserModal, UsersBulkAssignModal } from 'src/components/organisms'

import { useAuthority } from 'src/hooks'
import { ProfileEntity, InternalUserEntity, SelectableInternalUserEntity } from 'src/types'

const Container = styled.div`
  border-radius: 0 8px 8px 8px;
  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  overflow: hidden;
`

type Props = {
  users: InternalUserEntity[]
  sortItem: InternalUserSortItemType
  sortType: TableSortType
  selectSortItem: (newItem: InternalUserSortItemType) => void
  selectSortType: (newType: TableSortType) => void
  profile?: ProfileEntity
  afterOnSort?: () => void
  className?: string
}

export const InternalUserListTable: FC<Props> = ({
  users,
  sortItem,
  sortType,
  selectSortItem,
  selectSortType,
  profile,
  afterOnSort,
  className,
}) => {
  const [selectableInternalUsers, setSelectableInternalUsers] = useState<
    SelectableInternalUserEntity[]
  >(users.map(user => ({ ...user, isSelected: false })))
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)

  const { canManageUser } = useAuthority()

  const handleOnAllSelect = () => {
    if (!profile) {
      return
    }

    const isAllSelected = selectableInternalUsers.every(user => user.isSelected)
    const updatedInternalUsers = selectableInternalUsers.map(user => {
      return {
        ...user,
        isSelected: !isAllSelected,
      }
    })
    setSelectableInternalUsers(updatedInternalUsers)
  }

  const handleOnSelectUser = (selectedInternalUser: SelectableInternalUserEntity) => {
    const updatedInternalUsers = selectableInternalUsers.map(user => {
      return user.id === selectedInternalUser.id
        ? {
            ...user,
            isSelected: !selectedInternalUser.isSelected,
          }
        : user
    })
    setSelectableInternalUsers(updatedInternalUsers)
  }

  const handleSelectSortItem = (newItem: InternalUserSortItemType) => {
    if (sortItem === newItem) {
      const newSortType = sortType === 'desc' ? 'asc' : 'desc'
      selectSortType(newSortType)
      return
    }

    selectSortType('desc')
    selectSortItem(newItem)
    afterOnSort && afterOnSort()
  }

  const handleAfterAssigned = () => {
    setShowBulkAssignModal(false)
    setSelectableInternalUsers(previousUsers => {
      return previousUsers.map(user => ({ ...user, isSelected: false }))
    })
  }

  useEffect(() => {
    const updatedUsers = users.map(user => ({ ...user, isSelected: false }))
    setSelectableInternalUsers(updatedUsers)
  }, [users])

  return (
    <div className={className}>
      <Container className="tw-border tw-border-border-secondary">
        <InternalUserListTableHead
          internalUsers={selectableInternalUsers}
          sortItem={sortItem}
          sortType={sortType}
          onSelectItem={handleSelectSortItem}
          onClickAllSelect={handleOnAllSelect}
          className="tw-border-b tw-border-b-border-secondary"
        />
        {canManageUser && (
          <div className="tw-p-s0 tw-bg-surface-primary">
            <AddUserButton
              type="internal"
              onClick={() => setShowCreateUserModal(true)}
              className="tw-w-full"
            />
          </div>
        )}
        {profile && selectableInternalUsers.length > 0 ? (
          <InternalUserListTableBody
            users={selectableInternalUsers}
            onSelectUser={handleOnSelectUser}
          />
        ) : (
          <NotExistsUserMessage />
        )}
      </Container>

      <CreateUserModal isOpen={showCreateUserModal} onClose={() => setShowCreateUserModal(false)} />

      {showBulkAssignModal && (
        <UsersBulkAssignModal
          targetUserIds={selectableInternalUsers
            .filter(user => user.isSelected)
            .map(user => user.id)}
          isExternal={false}
          isOpen={showBulkAssignModal}
          onClose={() => setShowBulkAssignModal(false)}
          afterAssigned={handleAfterAssigned}
        />
      )}

      <BulkActionButton
        isActive={selectableInternalUsers.some(user => user.isSelected)}
        actions={[
          {
            text: 'プロジェクト追加',
            iconPath: 'plus/soft/line',
            action: () => setShowBulkAssignModal(true),
          },
        ]}
      />
    </div>
  )
}
